/**
 * LockSceneCapture — off-screen Three.js canvas that captures the PNG views
 * used by the PDF report: one 3/4 overview ("perspective") plus front, top
 * and the two opposite side views (§6), and a 3/4 exploded view without
 * beams (§5 CONNECTION GEOMETRY).
 *
 * Mount inside a FormProvider in a visibility:hidden div. Call onCapture once
 * and then unmount the component. It reads the form context exactly like
 * LockCalculator3DViewer so no extra data wiring is needed in the parent.
 *
 * Questo e' l'UNICO consumatore di `quote.planes` (30-09-2026): ogni vista
 * monta `<Quotes exportPlane=...>` col proprio piano, cosi' l'immagine porta
 * solo le quote leggibili in quella vista. Il viewer interattivo non filtra
 * nulla. Da qui la cattura una-vista-per-passo: il piano cambia il grafo di
 * scena, quindi serve un commit React fra uno scatto e il successivo.
 */

import { Canvas, useThree } from "@react-three/fiber";
import { FC, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import * as THREE from "three";

import type { LockCalculatorFormValues } from "../model/form";
import { useLockSceneInput, type FastenerSpecs, type TopDistanceBase } from "../model/toLockSceneInput";
import { Beam } from "./components/beams/Beam";
import { Fastener } from "./components/fasteners/Fastener";
import { ConnectorHeader } from "./components/locks/ConnectorHeader";
import { ConnectorJoist } from "./components/locks/ConnectorJoist";
import { ConnectorMeshOffset, applyConnectorMeshOffset } from "./components/locks/ConnectorMeshOffset";
import { GlbView } from "./components/locks/GlbView";
import { MeasureConnectorFlushThickness } from "./components/locks/MeasureConnectorFlushThickness";
import { Quotes } from "./components/quotes/Quotes";
import { COLORS } from "./config/colors";
import { ExplodeGuideLine } from "./components/fasteners/ExplodeGuideLine";
import { BEAM_OPACITY, EXPLODE_DEFAULTS, FOCUS_FRAMING, QUOTE_DEFAULTS } from "./config/defaults";
import type { ConnectorSide, Vec3 } from "./data/types";
import { useLockScene } from "./hooks/useLockScene";
import type { LockSceneInput } from "./LockScene.types";
import { connectorsFocus } from "./model/connectors";
import { explodedFastenerPoints, explodeLayoutForScene } from "./model/explode";
import { fastenerColorFor } from "./model/fasteners";
import type { LockstopInstance } from "./model/lockstopPlacement";
import { beamOwnerId, PER_VIEW_COPY_QUOTE_IDS, SIDE_CLEARANCE_QUOTE_IDS } from "./model/quotes";
import type { QuotePlane } from "./model/quotes.types";
import type { ResolvedScene } from "./model/types";

// ─── Public API ───────────────────────────────────────────────

/**
 * Le immagini del report. §6 3D INSTALLATION VIEWS, nell'ordine di
 * impaginazione: `perspective` a tutta larghezza, poi `front`/`top`, poi
 * `sideRight`/`sideLeft`. §5 CONNECTION GEOMETRY: `exploded` (vista esplosa
 * senza travi, 30-09-2026).
 */
export type OrthographicViews = {
  perspective: string;
  front: string;
  top: string;
  sideRight: string;
  sideLeft: string;
  exploded: string;
};

export type LockSceneCaptureProps = {
  fastenerSpecs?: FastenerSpecs;
  /** Lockstop catalog data needed by useLockScene to produce lockstop instances. */
  lockstopInstallationLevels?: number | null;
  lockstopCode?: string | null;
  screwEdgeThicknessMm?: number | null;
  /** The chosen lock module's c_min (mm) — default for the connector's distance from the joist top. */
  topDistanceBase?: TopDistanceBase | null;
  onCapture: (views: OrthographicViews) => void;
};

// ─── Constants ────────────────────────────────────────────────

// Risoluzione delle quattro viste tecniche (4:3, due per riga nel PDF).
const CAP_W = 600;
const CAP_H = 450;
// La panoramica occupa tutta la larghezza della pagina: con un 4:3 sarebbe
// alta ~135 mm e da sola mangerebbe mezza pagina, quindi si cattura larga.
const OVERVIEW_W = 1200;
const OVERVIEW_H = 450;

/** Una vista del report: direzione camera, "su" e piano quota da stampare.*/
interface CaptureView {
  key: keyof OrthographicViews;
  dir: THREE.Vector3;
  up: THREE.Vector3;
  width: number;
  height: number;
  /** Piano di `quote.planes` da filtrare; assente sulla panoramica (mostra tutto).*/
  plane?: QuotePlane;
  /** true → usa il focus sull'intera scena (travi incluse), non solo connettori.*/
  wideFrame?: boolean;
  /**
   * Sovrascrive `FOCUS_FRAMING.capture.marginFactor` per questa vista.
   * Valori < 1 allargano il frustum (camera più lontana), > 1 lo stringono.
   * Assente → usa il default (0.7).*/
  marginFactor?: number;
  /**
   * Dimensione della trave joist che la vista deve mettere in evidenza.
   * Il frustum si adatta dinamicamente affinché quella dimensione occupi
   * ~65 % dell'altezza visibile, col raggio connettori come soglia minima.
   * "joistH" = altezza (front), "joistB" = larghezza (top),
   * "joistMin" = la minore delle due (side).*/
  framingHint?: "joistH" | "joistB" | "joistMin";
  /**
   * true → vista esplosa (`explodeFactor = 1`, model/explode.ts) SENZA travi
   * e senza quote (le quote descrivono l'assieme montato), inquadrata
   * sull'ingombro reale di tutte le mesh visibili (`fitOrthoCameraToVisible`).*/
  exploded?: boolean;
  /**
   * true → inquadratura sull'ingombro reale di connettori, viti, lockstop e
   * quote stampate, travi ESCLUSE (restano sfondo, tagliate dal frame): le
   * quote finiscono sempre intere nell'immagine (30-09-2026). L'esploso lo
   * fa sempre.*/
  fitToContent?: boolean;
  /** Lato da non disegnare (trave, lock, viti e lockstop di quel lato). */
  hideSide?: ConnectorSide;
  /** true → nessuna quota in questa vista. */
  hideQuotes?: boolean;
  /** Id delle quote da non stampare in questa vista (vedi `Quotes.excludeIds`). */
  excludeQuoteIds?: readonly string[];
  /** Se presente, SOLO queste quote in questa vista (vedi `Quotes.includeIds`). */
  includeQuoteIds?: readonly string[];
}

// Direzioni di vista (versori): la distanza non e' fissa, si ricava dal
// raggio dei connettori cosi' che le viste inquadrino sempre l'assieme.
// La panoramica usa la stessa direzione 3/4 del preset "perspective" del
// viewer (`PRESET_DIRECTIONS` in LockScene.tsx) — camera ortografica anche
// li', come il viewer da 29-09-2026: e' una vista tecnica, non una foto.
const VIEWS: CaptureView[] = [
  {
    key: "perspective",
    dir: new THREE.Vector3(0.6247, 0.4685, 0.6247),
    up: new THREE.Vector3(0, 1, 0),
    width: OVERVIEW_W,
    height: OVERVIEW_H,
    wideFrame: true,
    // Inquadratura sulle quote (30-09-2026: "h = 300 mm" tagliata sul bordo).
    fitToContent: true,
    // Ogni misura UNA volta sola (30-09-2026, richiesta utente) — stessa
    // lista del viewer interattivo, vedi `PER_VIEW_COPY_QUOTE_IDS`.
    excludeQuoteIds: PER_VIEW_COPY_QUOTE_IDS,
  },
  // Front/top (30-09-2026, richiesta utente): quote dell'incastro — sezione
  // header b/h e distanze lock → trave (`jointClearanceQuotes`) — con
  // l'inquadratura adattata a includerle tutte. Le sezioni che vivono
  // all'estremita' lontana della trave restano fuori (allargherebbero il
  // frame fino alla fine della joist).
  {
    key: "front",
    dir: new THREE.Vector3(0, 0, 1),
    up: new THREE.Vector3(0, 1, 0),
    width: CAP_W,
    height: CAP_H,
    plane: "front",
    fitToContent: true,
    excludeQuoteIds: ["beam:joist:height", "beam:joist:base"],
  },
  {
    key: "top",
    dir: new THREE.Vector3(0, 1, 0),
    up: new THREE.Vector3(0, 0, -1),
    width: CAP_W,
    height: CAP_H,
    plane: "top",
    fitToContent: true,
    // Header "column": b e h sono in cima alla colonna, viste dall'alto
    // cadrebbero sopra il connettore.
    excludeQuoteIds: ["beam:joist:base", "beam:header:base", "beam:header:height"],
  },
  // Viste laterali con UN solo lato (30-09-2026, richiesta utente): la
  // destra (camera su +X) e' il LATO HEADER — trave/colonna header col suo
  // lock; la sinistra (camera su −X) e' il LATO JOIST. Solo le distanze
  // connettore → trave di quel lato (`SIDE_CLEARANCE_QUOTE_IDS`; l'altro
  // lato lo filtra `hideSide` via ownerId), inquadratura sul contenuto.
  {
    key: "sideRight",
    dir: new THREE.Vector3(1, 0, 0),
    up: new THREE.Vector3(0, 1, 0),
    width: CAP_W,
    height: CAP_H,
    plane: "side",
    fitToContent: true,
    hideSide: "joist",
    includeQuoteIds: SIDE_CLEARANCE_QUOTE_IDS,
  },
  {
    key: "sideLeft",
    dir: new THREE.Vector3(-1, 0, 0),
    up: new THREE.Vector3(0, 1, 0),
    width: CAP_W,
    height: CAP_H,
    plane: "side",
    fitToContent: true,
    hideSide: "header",
    includeQuoteIds: SIDE_CLEARANCE_QUOTE_IDS,
  },
  {
    // Stessa direzione 3/4 della panoramica: mostra la separazione lungo X
    // e la profondita' dei pezzi. Larga come la panoramica perche' l'esploso
    // si sviluppa lungo X.
    key: "exploded",
    dir: new THREE.Vector3(0.6247, 0.4685, 0.6247),
    up: new THREE.Vector3(0, 1, 0),
    width: OVERVIEW_W,
    height: OVERVIEW_H,
    exploded: true,
    fitToContent: true,
    hideQuotes: true,
  },
];

// Le etichette quota sono testo troika: la geometria dei glifi arriva DOPO il
// caricamento asincrono del font. Si aspetta che ogni testo in scena sia
// sincronizzato, con un tetto di frame oltre il quale si scatta comunque (un
// font irraggiungibile non deve bloccare il report).
const MAX_TEXT_SYNC_FRAMES = 180;
/** Frame da attendere dopo la sincronizzazione dei testi (chip + spostamento etichetta). */
const POST_SYNC_FRAMES = 3;

const EMPTY_INPUT: LockSceneInput = {
  lateralResistanceConfig: null,
  headerOrientation: null,
  connectorPairCount: 1,
  connectorPairLockItemIds: [null, null],
  header: { width: null, height: null },
  joist: { width: null, height: null, alphaDeg: null, betaDeg: null, connectorTopDistance: null, topOffsetFromHeader: null },
  fasteners: { headerItemId: null, joistItemId: null, inclinedItemId: null },
  lockstopItemId: null,
};

// ─── CaptureController (inside Canvas) ───────────────────────

/**
 * Scatta UNA vista: imposta la camera come default di r3f (serve ai
 * `<Billboard>` delle etichette, che si orientano su `state.camera`), attende
 * la sincronizzazione dei testi, renderizza e legge il canvas.
 */
function CaptureController({
  view,
  sceneReady,
  connectorFocus,
  sceneFocus,
  beamDims,
  onCaptured,
}: {
  /** Vista corrente; `null` quando sono finite (nulla da scattare). */
  view: CaptureView | null;
  sceneReady: boolean;
  /** Centro e raggio dei soli connettori — usato dalle 4 viste tecniche. */
  connectorFocus: { center: Vec3; radius: number };
  /** Centro e raggio dell'intera scena (travi incluse) — usato dalla panoramica. */
  sceneFocus: { center: Vec3; radius: number };
  /** Dimensioni joist per il framing dinamico; null se scena non ancora risolta. */
  beamDims: { joistB: number; joistH: number } | null;
  onCaptured: (key: keyof OrthographicViews, dataUrl: string) => void;
}) {
  const { gl, scene } = useThree();
  const setState = useThree((state) => state.set);

  useEffect(() => {
    if (!sceneReady || !view) return;

    // L'esploso parte dal focus connettori e viene reinquadrato nel tick.
    const focus = view.wideFrame ? sceneFocus : connectorFocus;
    const camera = buildCaptureCamera(view, focus, beamDims);
    // Camera di default di r3f: senza questo i billboard delle etichette si
    // orienterebbero sulla camera iniziale del Canvas, non su questa vista.
    setState({ camera });

    let cancelled = false;
    let frames = 0;
    let framesSinceSync = 0;

    const tick = () => {
      if (cancelled) return;
      frames += 1;
      const synced = textLabelsSynced(scene);
      framesSinceSync = synced ? framesSinceSync + 1 : 0;
      // Dopo la sincronizzazione servono ancora un paio di frame: il chip
      // dell'etichetta si monta al commit React successivo e `QuoteLabel` la
      // sposta accanto alla linea in `useFrame` (dimensioni del chip note).
      if (frames < MAX_TEXT_SYNC_FRAMES && framesSinceSync < POST_SYNC_FRAMES) {
        requestAnimationFrame(tick);
        return;
      }
      // Inquadratura sull'ingombro REALE di cio' che e' visibile (lock,
      // lockstop, viti — anche le inclinate esplose, in diagonale — linee
      // guida e quote), travi escluse, misurato sul grafo gia' renderizzato
      // (quindi DOPO la sincronizzazione dei testi delle quote).
      if (view.fitToContent) fitOrthoCameraToVisible(camera, scene, view, CONTENT_FIT_PADDING);
      bringQuotesToFront(scene);
      gl.setSize(view.width, view.height, false);
      gl.setClearColor(0xffffff, 1);
      gl.clear();
      gl.render(scene, camera);
      onCaptured(view.key, gl.domElement.toDataURL("image/png"));
    };

    // Due rAF annidati: il primo lascia a r3f il tempo di renderizzare il
    // nuovo grafo di scena, il secondo che il frame sia composito.
    const handle = requestAnimationFrame(() => requestAnimationFrame(tick));
    return () => {
      cancelled = true;
      cancelAnimationFrame(handle);
    };
  }, [view, sceneReady, connectorFocus, sceneFocus, beamDims, gl, scene, setState, onCaptured]);

  return null;
}

/**
 * Camera ORTOGRAFICA inquadrata sui connettori: sono viste tecniche, e con una
 * prospettiva una vite vista di punta si allunga a raggiera man mano che si
 * allontana dall'asse ottico, tanto piu' quanto e' largo il fov.
 */
function buildCaptureCamera(
  view: CaptureView,
  focus: { center: Vec3; radius: number },
  beamDims?: { joistB: number; joistH: number } | null,
): THREE.OrthographicCamera {
  const target = new THREE.Vector3(focus.center[0], focus.center[1], focus.center[2]);
  const radius = Math.max(focus.radius, FOCUS_FRAMING.capture.minRadiusMm);
  // Semi-altezza del frustum: il marginFactor per-vista sovrascrive il
  // default; valori più bassi allargano l'inquadratura (camera più lontana).
  const margin = view.marginFactor ?? FOCUS_FRAMING.capture.marginFactor;
  let halfHeight = radius * margin;

  if (view.framingHint && beamDims) {
    let targetMm: number;
    let pad: number;
    switch (view.framingHint) {
      case "joistH": targetMm = beamDims.joistH; pad = 0.9; break;
      case "joistB": targetMm = beamDims.joistB; pad = 0.9; break;
      case "joistMin": targetMm = Math.min(beamDims.joistB, beamDims.joistH); pad = 1.4; break;
    }
    halfHeight = Math.max(halfHeight, targetMm * pad);
  }

  const halfWidth = halfHeight * (view.width / view.height);
  // In ortografica la distanza non cambia la scala: serve solo a tenere la
  // scena dentro near/far.
  const distance = Math.max(radius, halfHeight) * 10;

  const camera = new THREE.OrthographicCamera(-halfWidth, halfWidth, halfHeight, -halfHeight, 1, distance * 4);
  camera.position.copy(target).addScaledVector(view.dir, distance);
  camera.up.copy(view.up);
  camera.lookAt(target);
  camera.updateProjectionMatrix();
  return camera;
}

/** Margine attorno all'ingombro nelle viste `fitToContent` (1 = a filo). */
const CONTENT_FIT_PADDING = 1.08;

/** `userData` che esclude un sottoalbero dal calcolo dell'inquadratura (le travi). */
const FIT_IGNORE = { fitIgnore: true } as const;

/** `userData` del gruppo quote: `bringQuotesToFront` lo disegna sopra a tutto. */
const QUOTES_OVERLAY = { quotesOverlay: true } as const;
/** Sopra le travi (renderOrder 0) nella lista dei trasparenti. */
const QUOTES_RENDER_ORDER = 10;

/**
 * Nel report ogni quota deve leggersi (30-09-2026, bug: etichette vuote nella
 * top e quote sparite nella perspective — stavano dentro il volume di una
 * trave semitrasparente, che le copriva). Solo qui, non nei componenti delle
 * quote (condivisi col viewer, dove l'occlusione resta voluta): i materiali
 * del gruppo quote passano nella lista dei trasparenti con `renderOrder`
 * alto, quindi DOPO le travi, e senza depth test. Il chip resta sotto al
 * testo: stesso renderOrder, ordinati per profondita' (il chip e' arretrato).
 */
function bringQuotesToFront(root: THREE.Object3D): void {
  root.traverse((object) => {
    if (!object.userData["quotesOverlay"]) return;
    object.traverse((child) => {
      const material = (child as { material?: THREE.Material | THREE.Material[] }).material;
      if (!material) return;
      child.renderOrder = QUOTES_RENDER_ORDER;
      for (const m of Array.isArray(material) ? material : [material]) {
        m.transparent = true;
        m.depthTest = false;
        m.depthWrite = false;
      }
    });
  });
}

/**
 * Ingombro world degli oggetti VISIBILI con geometria (mesh e linee),
 * saltando i sottoalberi nascosti (`visible={false}` — `Box3.setFromObject`
 * non guarda la visibilita') e quelli marcati `FIT_IGNORE` (le travi).
 */
function visibleBounds(root: THREE.Object3D): THREE.Box3 {
  const box = new THREE.Box3();
  const objectBox = new THREE.Box3();
  root.updateMatrixWorld(true);
  const visit = (object: THREE.Object3D) => {
    if (!object.visible || object.userData["fitIgnore"]) return;
    // Testo troika: il bounding box della sua geometria non e' affidabile,
    // si usano i `blockBounds` (locali, gia' ancorati) piu' il padding del
    // chip di `QuoteLabel` — altrimenti un'etichetta finisce tagliata sul bordo.
    const blockBounds = (object as { textRenderInfo?: { blockBounds?: ArrayLike<number> } }).textRenderInfo
      ?.blockBounds;
    const geometry = (object as { geometry?: THREE.BufferGeometry }).geometry;
    if (blockBounds && blockBounds.length >= 4) {
      const pad = QUOTE_DEFAULTS.labelSizeMm * QUOTE_DEFAULTS.labelChipPaddingRatio;
      objectBox
        .set(
          new THREE.Vector3((blockBounds[0] ?? 0) - pad, (blockBounds[1] ?? 0) - pad, 0),
          new THREE.Vector3((blockBounds[2] ?? 0) + pad, (blockBounds[3] ?? 0) + pad, 0),
        )
        .applyMatrix4(object.matrixWorld);
      box.union(objectBox);
    } else if (geometry) {
      if (!geometry.boundingBox) geometry.computeBoundingBox();
      if (geometry.boundingBox && !geometry.boundingBox.isEmpty()) {
        objectBox.copy(geometry.boundingBox).applyMatrix4(object.matrixWorld);
        box.union(objectBox);
      }
    }
    for (const child of object.children) visit(child);
  };
  visit(root);
  return box;
}

/**
 * Reinquadra la camera ortografica sull'ingombro visibile di `root`: centro
 * sul centro del box, semi-dimensioni dalla proiezione degli 8 spigoli nello
 * spazio camera (un box proiettato e' simmetrico rispetto al proprio centro),
 * rispettando l'aspect della vista. Box vuoto → camera invariata.
 */
function fitOrthoCameraToVisible(
  camera: THREE.OrthographicCamera,
  root: THREE.Object3D,
  view: CaptureView,
  padding: number,
): void {
  const box = visibleBounds(root);
  if (box.isEmpty()) return;

  const center = box.getCenter(new THREE.Vector3());
  const diagonal = box.getSize(new THREE.Vector3()).length();
  const distance = diagonal * 2 + 100;
  camera.position.copy(center).addScaledVector(view.dir, distance);
  camera.up.copy(view.up);
  camera.lookAt(center);
  camera.updateMatrixWorld(true);

  let halfX = 0;
  let halfY = 0;
  const corner = new THREE.Vector3();
  for (let i = 0; i < 8; i += 1) {
    corner
      .set(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z)
      .applyMatrix4(camera.matrixWorldInverse);
    halfX = Math.max(halfX, Math.abs(corner.x));
    halfY = Math.max(halfY, Math.abs(corner.y));
  }

  const aspect = view.width / view.height;
  const halfHeight = Math.max(halfY, halfX / aspect) * padding;
  const halfWidth = halfHeight * aspect;
  camera.left = -halfWidth;
  camera.right = halfWidth;
  camera.top = halfHeight;
  camera.bottom = -halfHeight;
  camera.near = 1;
  camera.far = distance * 2 + diagonal;
  camera.updateProjectionMatrix();
}

/**
 * Tutti i testi troika presenti in scena hanno gia' la loro geometria?
 * Riconosciuti per duck-typing (`text` + `textRenderInfo`) invece di
 * importare troika: e' una dipendenza indiretta di drei, non del progetto.
 */
function textLabelsSynced(root: THREE.Object3D): boolean {
  let synced = true;
  root.traverse((object) => {
    const candidate = object as { text?: unknown; textRenderInfo?: unknown };
    if (typeof candidate.text === "string" && !candidate.textRenderInfo) synced = false;
  });
  return synced;
}

// ─── SceneGeometry (inside Suspense, inside Canvas) ──────────
//
// IMPORTANTE (30-09-2026, bug "assieme diverso dal viewer"): la cattura deve
// replicare la STESSA pipeline di posizionamento di `SceneContent` in
// `LockScene.tsx` — ConnectorMeshOffset → MeasureConnectorFlushThickness → viti
// corrette → lockstop — senza toggle/debug. L'esploso (solo per la vista
// `exploded`) usa gli STESSI helper del viewer (`model/explode.ts`).

function SceneGeometry({
  resolvedScene,
  lockstops,
  hasData,
  quotePlane,
  exploded = false,
  hideSide,
  hideQuotes = false,
  excludeQuoteIds,
  includeQuoteIds,
  onReady,
}: {
  resolvedScene: ResolvedScene | null;
  lockstops: LockstopInstance[];
  hasData: boolean;
  quotePlane?: QuotePlane;
  /** true → vista esplosa a `explodeFactor = 1`, senza travi. */
  exploded?: boolean;
  /** Lato da non disegnare (vedi `CaptureView.hideSide`). */
  hideSide?: ConnectorSide;
  hideQuotes?: boolean;
  excludeQuoteIds?: readonly string[];
  includeQuoteIds?: readonly string[];
  onReady: () => void;
}) {
  // Le quote del lato nascosto spariscono con lui (ownerId della trave).
  const quoteVisibility = useMemo(
    () => (hideSide ? { [beamOwnerId(hideSide)]: false } : undefined),
    [hideSide],
  );
  const readyCalled = useRef(false);

  useEffect(() => {
    if (hasData && !readyCalled.current) {
      readyCalled.current = true;
      onReady();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasData]);

  if (!hasData || !resolvedScene) return null;

  return (
    <group>
      {/* Travi: nascoste nell'esploso e sul lato `hideSide`; fuori dal calcolo
          dell'inquadratura (FIT_IGNORE) — sono sfondo (30-09-2026). */}
      <group userData={FIT_IGNORE}>
        <Beam
          spec={resolvedScene.beams.header}
          color={COLORS.headerBeam}
          showDebug={false}
          opacity={BEAM_OPACITY}
          visible={!exploded && hideSide !== "header"}
        />
        <Beam
          spec={resolvedScene.beams.joist}
          color={COLORS.joistBeam}
          showDebug={false}
          opacity={BEAM_OPACITY}
          visible={!exploded && hideSide !== "joist"}
        />
      </group>

      {/* Pipeline di posizionamento: stessa catena render-prop del viewer
          (ConnectorMeshOffset → MeasureConnectorFlushThickness ×3) — offset
          bbox reale + spessore misurato → posizioni corrette per connettori,
          viti e lockstop. */}
      <ConnectorMeshOffset url={resolvedScene.connectors.find((c) => c.index === 0)?.glbUrl ?? null}>
        {(offset0) => (
          <ConnectorMeshOffset url={resolvedScene.connectors.find((c) => c.index === 1)?.glbUrl ?? null}>
            {(offset1) => {
              const rawCenterByIndex: Record<number, THREE.Vector3> = { 0: offset0, 1: offset1 };
              const lockstopCoverageByOwner = buildLockstopCoverage(lockstops);
              const probeForIndex = (index: number) => {
                const owner = resolvedScene.connectors.find((c) => c.index === index);
                const hole = owner?.features.holes.find((h) => h.type === "standard");
                return {
                  url: owner?.glbUrl ?? null,
                  probeXY: hole ? ([hole.position[0], hole.position[1]] as [number, number]) : null,
                };
              };
              const probe0 = probeForIndex(0);
              const probe1 = probeForIndex(1);
              const lockstopProbe = {
                url: lockstops[0]?.glbUrl ?? null,
                probeXY: lockstops[0]?.holesXY[0] ?? null,
              };
              return (
                <MeasureConnectorFlushThickness url={probe0.url} probeXY={probe0.probeXY}>
                  {(measured0) => (
                    <MeasureConnectorFlushThickness url={probe1.url} probeXY={probe1.probeXY}>
                      {(measured1) => (
                        <MeasureConnectorFlushThickness url={lockstopProbe.url} probeXY={lockstopProbe.probeXY}>
                          {(measuredLockstopThicknessMm) => {
                            const measuredThicknessByIndex: Record<number, number | null> = {
                              0: measured0,
                              1: measured1,
                            };
                            // Esploso: stessa regola del viewer (spessore lockstop
                            // misurato, fallback sul dichiarato); factor 0 = assemblato.
                            const explodeLayout = explodeLayoutForScene(
                              resolvedScene,
                              lockstops,
                              lockstops[0] ? (measuredLockstopThicknessMm ?? lockstops[0].thicknessMm) : 0,
                              EXPLODE_DEFAULTS.gapMm,
                              exploded ? 1 : 0,
                            );
                            return (
                              <>
                                {/* ── Connettori (posizione corretta con bbox offset) ── */}
                                {resolvedScene.connectors.map((instance) => {
                                  const correctedPosition = applyConnectorMeshOffset(
                                    [
                                      instance.position[0] + explodeLayout[instance.side].connectorOffsetXMm,
                                      instance.position[1],
                                      instance.position[2],
                                    ],
                                    rawCenterByIndex[instance.index] ?? offset0,
                                    instance.quaternion,
                                    instance.meshQuaternion,
                                  );
                                  return instance.side === "joist" ? (
                                    <ConnectorJoist
                                      key={`joist-${instance.index}`}
                                      instance={instance}
                                      position={correctedPosition}
                                      color={COLORS.connectorJoist}
                                      showDebug={false}
                                      visible={hideSide !== "joist"}
                                    />
                                  ) : (
                                    <ConnectorHeader
                                      key={`header-${instance.index}`}
                                      instance={instance}
                                      position={correctedPosition}
                                      color={COLORS.connectorHeader}
                                      showDebug={false}
                                      visible={hideSide !== "header"}
                                    />
                                  );
                                })}

                                {/* ── Viti (stessa logica lockstop + mesh offset del viewer) ── */}
                                {resolvedScene.fasteners.map((anchor) => {
                                  // Le inclinate hanno il foro sul lock joist: stanno col lato joist.
                                  if (anchor.side === hideSide) return null;
                                  const isInclined = anchor.holeType === "inclined";
                                  const coverage = isInclined
                                    ? undefined
                                    : lockstopCoverageByOwner.get(`${anchor.side}:${anchor.connectorIndex}`);
                                  const isCovered = !!coverage?.holeIds.has(anchor.holeId);
                                  const lockstopThicknessMm = isCovered
                                    ? (measuredLockstopThicknessMm ?? coverage!.thicknessMm)
                                    : 0;
                                  const anchorPosition: [number, number, number] = [
                                    anchor.position[0] - anchor.axis[0] * lockstopThicknessMm,
                                    anchor.position[1] - anchor.axis[1] * lockstopThicknessMm,
                                    anchor.position[2] - anchor.axis[2] * lockstopThicknessMm,
                                  ];
                                  const owner = resolvedScene.connectors.find(
                                    (c) => c.side === anchor.side && c.index === anchor.connectorIndex,
                                  );
                                  const ownerRawCenter = rawCenterByIndex[anchor.connectorIndex] ?? offset0;
                                  const correct = (point: Vec3): Vec3 =>
                                    owner
                                      ? applyConnectorMeshOffset(point, ownerRawCenter, owner.quaternion, owner.meshQuaternion)
                                      : point;
                                  const { screw, hole } = explodedFastenerPoints(
                                    anchor,
                                    anchorPosition,
                                    explodeLayout[anchor.side],
                                  );
                                  const correctedPosition = correct(screw);
                                  return (
                                    <group key={anchor.id}>
                                      <Fastener
                                        anchor={anchor}
                                        position={correctedPosition}
                                        color={fastenerColorFor(resolvedScene.fastenerLegend, anchor.fastenerItemId)}
                                        showDebug={false}
                                        visible
                                      />
                                      {exploded ? <ExplodeGuideLine from={correct(hole)} to={correctedPosition} /> : null}
                                    </group>
                                  );
                                })}

                                {/* ── Lockstop (stessa logica Z-corrected + bbox offset del viewer) ── */}
                                {lockstops.map((lockstop) => {
                                  if (lockstop.side === hideSide) return null;
                                  const owner = resolvedScene.connectors.find(
                                    (c) => c.side === lockstop.side && c.index === lockstop.connectorIndex,
                                  );
                                  const ownerRawCenter = rawCenterByIndex[lockstop.connectorIndex] ?? offset0;
                                  const measuredThicknessMm = measuredThicknessByIndex[lockstop.connectorIndex] ?? null;
                                  const zCorrectedPosition: Vec3 =
                                    measuredThicknessMm != null
                                      ? (() => {
                                          const delta = measuredThicknessMm - lockstop.localAnchorZMm;
                                          const worldZAxis = new THREE.Vector3(0, 0, 1).applyQuaternion(
                                            lockstop.quaternion,
                                          );
                                          return [
                                            lockstop.position[0] + delta * worldZAxis.x,
                                            lockstop.position[1] + delta * worldZAxis.y,
                                            lockstop.position[2] + delta * worldZAxis.z,
                                          ];
                                        })()
                                      : lockstop.position;
                                  const explodedPosition: Vec3 = [
                                    zCorrectedPosition[0] + explodeLayout[lockstop.side].lockstopOffsetXMm,
                                    zCorrectedPosition[1],
                                    zCorrectedPosition[2],
                                  ];
                                  const correctedPosition = owner
                                    ? applyConnectorMeshOffset(
                                        explodedPosition,
                                        ownerRawCenter,
                                        owner.quaternion,
                                        owner.meshQuaternion,
                                      )
                                    : explodedPosition;
                                  return (
                                    <group
                                      key={lockstop.id}
                                      position={correctedPosition}
                                      quaternion={lockstop.quaternion}
                                    >
                                      {lockstop.glbUrl ? (
                                        <GlbView url={lockstop.glbUrl} />
                                      ) : (
                                        <mesh>
                                          <boxGeometry
                                            args={[lockstop.widthMm, lockstop.heightMm, lockstop.thicknessMm]}
                                          />
                                          <meshStandardMaterial
                                            color={COLORS.lockstop}
                                            roughness={0.5}
                                            metalness={0.6}
                                          />
                                        </mesh>
                                      )}
                                    </group>
                                  );
                                })}
                              </>
                            );
                          }}
                        </MeasureConnectorFlushThickness>
                      )}
                    </MeasureConnectorFlushThickness>
                  )}
                </MeasureConnectorFlushThickness>
              );
            }}
          </ConnectorMeshOffset>
        )}
      </ConnectorMeshOffset>

      {/* Quote filtrate sul piano della vista; assenti dove la vista lo chiede
          (esploso — descrivono l'assieme montato — e viste laterali). */}
      {hideQuotes ? null : (
        <group userData={QUOTES_OVERLAY}>
          <Quotes
            scene={resolvedScene}
            exportPlane={quotePlane}
            visibility={quoteVisibility}
            excludeIds={excludeQuoteIds}
            includeIds={includeQuoteIds}
          />
        </group>
      )}
    </group>
  );
}

/** Mappa lockstop-coverage per lato+indice, stessa logica di SceneContent. */
function buildLockstopCoverage(lockstops: LockstopInstance[]) {
  const map = new Map<string, { thicknessMm: number; holeIds: Set<string> }>();
  for (const lockstop of lockstops) {
    // Con 2 connettori un pezzo puo' coprire fori di entrambi: la chiave e' del connettore del foro.
    for (const { connectorIndex, holeId } of lockstop.coveredHoles) {
      const key = `${lockstop.side}:${connectorIndex}`;
      const entry = map.get(key) ?? { thicknessMm: lockstop.thicknessMm, holeIds: new Set<string>() };
      entry.holeIds.add(holeId);
      map.set(key, entry);
    }
  }
  return map;
}

// ─── CaptureCanvas (manages sceneReady state, renders Canvas) ─

function CaptureCanvas({
  resolvedScene,
  lockstops,
  hasData,
  onCaptureRef,
}: {
  resolvedScene: ResolvedScene | null;
  lockstops: LockstopInstance[];
  hasData: boolean;
  onCaptureRef: React.MutableRefObject<(views: OrthographicViews) => void>;
}) {
  const [sceneReady, setSceneReady] = useState(false);
  const handleReady = useCallback(() => setSceneReady(true), []);

  // Indice della vista in corso: avanza di uno scatto per volta perche' ogni
  // vista monta un set di quote diverso e serve un commit React fra le due.
  const [step, setStep] = useState(0);
  const capturesRef = useRef<Partial<OrthographicViews>>({});
  const currentView = VIEWS[step] ?? null;

  const handleCaptured = useCallback((key: keyof OrthographicViews, dataUrl: string) => {
    capturesRef.current[key] = dataUrl;
    setStep((previous) => previous + 1);
  }, []);

  const delivered = useRef(false);
  useEffect(() => {
    if (currentView || delivered.current || step === 0) return;
    delivered.current = true;
    onCaptureRef.current(capturesRef.current as OrthographicViews);
    // onCaptureRef e' un ref (stabile)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, step]);

  // Dimensioni joist per il framing dinamico delle viste tecniche.
  const beamDims = useMemo<{ joistB: number; joistH: number } | null>(() => {
    if (!resolvedScene) return null;
    const { b, h } = resolvedScene.beams.joist.dims;
    return { joistB: b, joistH: h };
  }, [resolvedScene]);

  // Inquadratura connettori: usata dalle 4 viste tecniche (front/top/side×2).
  const connectorFocus = useMemo(() => connectorsFocus(resolvedScene?.connectors ?? []), [resolvedScene]);

  // Inquadratura scena intera (travi incluse): usata dalla panoramica per
  // catturare l'assemblaggio completo, non solo la zona connettori.
  const sceneFocus = useMemo<{ center: Vec3; radius: number }>(() => {
    if (!resolvedScene) return connectorFocus;
    const cf = connectorFocus;
    const hL = resolvedScene.beams.header.dims.l;
    const jL = resolvedScene.beams.joist.dims.l;
    const maxBeam = Math.max(hL, jL);
    // Il raggio deve coprire le estremità delle travi: la trave più lunga
    // si estende fino a ±l/2 dal centro connettori (le travi sono centrate
    // sull'assieme). Si prende il massimo fra il raggio connettori e la
    // metà della trave più lunga, con un margine di sicurezza.
    const sceneRadius = Math.max(cf.radius, maxBeam / 2 + 20);
    return { center: cf.center, radius: sceneRadius };
  }, [resolvedScene, connectorFocus]);

  // Questo canvas monta e smonta a ogni cattura: R3F chiama forceContextLoss()
  // da se', ma solo 500 ms dopo lo smontaggio (unmountComponentAtNode), quindi
  // senza rilasciare qui lo slot GPU subito i cicli di cattura ravvicinati
  // esaurirebbero il limite di contesti WebGL e il viewer perde il suo.
  // Dopo la nostra chiamata la sua diventa un no-op: sul contesto gia' perso
  // Chrome stampa "WebGL: INVALID_OPERATION: loseContext: context already lost".
  const glRef = useRef<THREE.WebGLRenderer | null>(null);
  useEffect(
    () => () => {
      const gl = glRef.current;
      if (!gl) return;
      try {
        gl.forceContextLoss();
        gl.forceContextLoss = () => undefined;
        gl.dispose();
      } catch {
        /* ignore */
      }
      glRef.current = null;
    },
    [],
  );

  return (
    <Canvas
      gl={{ antialias: true, preserveDrawingBuffer: true }}
      camera={{ position: [800, 600, 800], fov: 40, near: 1, far: 10000 }}
      style={{ width: OVERVIEW_W, height: OVERVIEW_H }}
      onCreated={({ gl }) => {
        glRef.current = gl;
      }}
    >
      <ambientLight intensity={0.6} />
      <directionalLight position={[5, 10, 5]} intensity={1.2} />
      <Suspense fallback={null}>
        <SceneGeometry
          resolvedScene={resolvedScene}
          lockstops={lockstops}
          hasData={hasData}
          quotePlane={currentView?.plane}
          exploded={currentView?.exploded ?? false}
          hideSide={currentView?.hideSide}
          hideQuotes={currentView?.hideQuotes ?? false}
          excludeQuoteIds={currentView?.excludeQuoteIds}
          includeQuoteIds={currentView?.includeQuoteIds}
          onReady={handleReady}
        />
      </Suspense>
      <CaptureController
        view={currentView}
        sceneReady={sceneReady}
        connectorFocus={connectorFocus}
        sceneFocus={sceneFocus}
        beamDims={beamDims}
        onCaptured={handleCaptured}
      />
    </Canvas>
  );
}

// ─── LockSceneCapture (public) ────────────────────────────────

/**
 * Mount inside FormProvider inside a visibility:hidden div.
 * Calls onCapture once with the five PNG data-URL strings, then the parent
 * should unmount this component.
 */
export const LockSceneCapture: FC<LockSceneCaptureProps> = ({
  fastenerSpecs,
  lockstopInstallationLevels,
  lockstopCode,
  screwEdgeThicknessMm,
  topDistanceBase,
  onCapture,
}) => {
  const { control } = useFormContext<LockCalculatorFormValues>();
  const form = useWatch({ control }) as LockCalculatorFormValues | undefined;

  const input = useLockSceneInput(form, fastenerSpecs, undefined, topDistanceBase);

  const { scene, lockstops, hasData } = useLockScene(input ?? EMPTY_INPUT, {
    installationLevels: lockstopInstallationLevels ?? null,
    code: lockstopCode ?? null,
    screwEdgeThicknessMm: screwEdgeThicknessMm ?? null,
  });

  const onCaptureRef = useRef(onCapture);
  onCaptureRef.current = onCapture;

  if (!input) return null;

  return <CaptureCanvas resolvedScene={scene} lockstops={lockstops} hasData={hasData} onCaptureRef={onCaptureRef} />;
};
